// API service for prescriptions management
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class PrescriptionService {
  static async getPrescriptionsByPatient(patientId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/prescriptions/patient/${patientId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch prescriptions');
      }

      return result;
    } catch (error) {
      console.error('Error in getPrescriptionsByPatient:', error);
      throw error;
    }
  }

  static async getPrescriptionById(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/prescriptions/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch prescription');
      }

      return result;
    } catch (error) {
      console.error('Error in getPrescriptionById:', error);
      throw error;
    }
  }

  static async createPrescription(data, token, idempotencyKey = null) {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Pass through the idempotency key so the backend can return the
      // previously-created prescription when the client retries after a
      // network failure. Without this, a transient error plus a retry
      // would silently produce duplicate prescriptions.
      if (idempotencyKey) {
        headers['X-Idempotency-Key'] = idempotencyKey;
      }

      const response = await fetch(`${API_BASE_URL}/prescriptions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create prescription');
      }

      return result;
    } catch (error) {
      console.error('Error in createPrescription:', error);
      throw error;
    }
  }

  static async updatePrescription(id, data, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/prescriptions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update prescription');
      }

      return result;
    } catch (error) {
      console.error('Error in updatePrescription:', error);
      throw error;
    }
  }

  static async markAsPrinted(id, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/prescriptions/${id}/mark-printed`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to mark prescription as printed');
      }

      return result;
    } catch (error) {
      console.error('Error in markAsPrinted:', error);
      throw error;
    }
  }

  static async cancelPrescription(id, reason, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/prescriptions/${id}/cancel`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to cancel prescription');
      }

      return result;
    } catch (error) {
      console.error('Error in cancelPrescription:', error);
      throw error;
    }
  }


  static async getAllPrescriptions(params = {}, token) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
      if (params.status) queryParams.append('status', params.status);
      
      const url = `${API_BASE_URL}/prescriptions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch prescriptions');
      }

      return result;
    } catch (error) {
      console.error('Error in getAllPrescriptions:', error);
      throw error;
    }
  }

  static async getPrescriptionStats(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/prescriptions/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch prescription stats');
      }

      return result;
    } catch (error) {
      console.error('Error in getPrescriptionStats:', error);
      throw error;
    }
  }
}

export default PrescriptionService;
